/**
 * Event Bus
 * Centralized event system for process thread business domain events
 *
 * Allows subscribing to and publishing process thread events
 */

import { ProcessThreadRenderEvent, ProcessThreadRenderEventHandler } from './events/workflowRenderEvents';

/**
 * Event Bus Class
 */
class EventBus {
  private handlers: Map<string, Set<ProcessThreadRenderEventHandler>> = new Map();

  /**
   * Subscribe to an event type
   */
  subscribe(eventType: string, handler: ProcessThreadRenderEventHandler): () => void {
    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, new Set());
    }

    this.handlers.get(eventType)!.add(handler);

    // Return unsubscribe function
    return () => {
      this.handlers.get(eventType)?.delete(handler);
    };
  }

  /**
   * Publish an event
   */
  async publish(event: ProcessThreadRenderEvent): Promise<void> {
    const eventType = event.eventType;
    const handlers = this.handlers.get(eventType);

    if (!handlers || handlers.size === 0) {
      return;
    }

    // Execute all handlers
    const promises: Promise<void>[] = [];
    for (const handler of handlers) {
      try {
        const result = handler(event);
        if (result instanceof Promise) {
          promises.push(result);
        }
      } catch (error) {
        // Ignore handler errors
      }
    }

    // Wait for all async handlers to complete
    if (promises.length > 0) {
      await Promise.all(promises);
    }
  }

  /**
   * Subscribe to all events
   */
  subscribeToAll(handler: ProcessThreadRenderEventHandler): () => void {
    const eventTypes = [
      'ProjectRendered',
      'ProcessRendered',
      'ProcessThreadRendered',
      'ProcessThreadDataLoaded',
    ];

    const unsubscribers = eventTypes.map(type => this.subscribe(type, handler));

    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }

  /**
   * Clear all handlers
   */
  clear(): void {
    this.handlers.clear();
  }

  /**
   * Get handler count for debugging
   */
  getHandlerCount(eventType: string): number {
    return this.handlers.get(eventType)?.size || 0;
  }
}

// Singleton instance
export const eventBus = new EventBus();

/**
 * Convenience functions for common events
 */
export const publishProjectRendered = (context: Omit<import('./events/workflowRenderEvents').ProjectRenderedEvent, 'eventType' | 'timestamp'>): Promise<void> => {
  return eventBus.publish({
    ...context,
    eventType: 'ProjectRendered',
    timestamp: new Date(),
  });
};

export const publishProcessRendered = (context: Omit<import('./events/workflowRenderEvents').ProcessRenderedEvent, 'eventType' | 'timestamp'>): Promise<void> => {
  return eventBus.publish({
    ...context,
    eventType: 'ProcessRendered',
    timestamp: new Date(),
  });
};

export const publishProcessThreadRendered = (context: Omit<import('./events/workflowRenderEvents').ProcessThreadRenderedEvent, 'eventType' | 'timestamp'>): Promise<void> => {
  return eventBus.publish({
    ...context,
    eventType: 'ProcessThreadRendered',
    timestamp: new Date(),
  });
};

export const publishProcessThreadDataLoaded = (context: Omit<import('./events/workflowRenderEvents').ProcessThreadDataLoadedEvent, 'eventType' | 'timestamp'>): Promise<void> => {
  return eventBus.publish({
    ...context,
    eventType: 'ProcessThreadDataLoaded',
    timestamp: new Date(),
  });
};
